import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { BlogContent, RelatedPosts, ScrollNavigation, TableOfContents } from '../../components/blogs';
import { Breadcrumb, OptimizedImage, Loader } from '../../components/common';
import { FooterSection, HeaderSection } from '../../components/layout';
import styles from '../../styles/components/blogs/BlogDetail.module.css';
import { fetchBlogBySlug, fetchConfigPage } from '../../utils/api';
import { formatDate, getCategoryNames } from '../../utils/helpers/blogUtils';
import SEOHead, { getBlogSEO } from '../../utils/helpers/seo';
import { useBlogScroll } from '../../utils/hooks/useBlogScroll';
import { useDarkMode } from '../../utils/hooks/useDarkMode';
import { useTableOfContents } from '../../utils/hooks/useTableOfContents';

export default function BlogsDetails({ initialData, error }) {
    const router = useRouter();
    const [strapiData, setStrapiData] = useState(initialData);
    const [isLoading, setIsLoading] = useState(!initialData);
    const [apiError, setError] = useState(error);

    // Dark mode hook
    const { isDarkMode, isThemeInitialized } = useDarkMode();

    // Handle initialData changes
    useEffect(() => {
        if (initialData) {
            setStrapiData(initialData);
            setIsLoading(false);
            setError(null);
        }
    }, [initialData]);

    // Extract content from Strapi data when available
    const configData = strapiData?.configData || {};
    const header = configData.header || {};
    const footer = configData.footer || {};
    const blog = strapiData?.blog || null;
    const relatedBlogs = blog?.related_posts || [];



    // Custom hooks for table of contents and scroll tracking
    const { tableOfContents } = useTableOfContents(blog, '.blogDetail');
    const activeHeading = useBlogScroll(tableOfContents);

    // Priority 1: Show error if there's an error
    if (apiError) {
        return <Loader type="error" message="Oops! Something Went Wrong" description="Please wait a moment and try again. We'll be back up shortly!" fullPage={true} action={() => window.location.reload()} actionText="Refresh Page" />;
    }

    // Priority 2: Show loading if no data yet
    if (isLoading || !initialData) {
        return <Loader type="loading" message="Loading blog post..." description="Please wait while we fetch the article content." fullPage={true} />;
    }

    // Priority 3: Show not found state if blog doesn't exist
    if (!blog) {
        return <Loader type="empty" message="Blog Not Found" description="The blog post you're looking for doesn't exist or has been removed." fullPage={true} action={() => router.push('/blogs')} actionText="Refresh Page" />;
    }

    const breadcrumbs = [
        { name: 'Home', url: '/' },
        { name: 'Blogs', url: '/blogs' },
        { name: blog.title, url: null }
    ];

    // Generate SEO data for this blog post
    const seoData = getBlogSEO(blog, configData, strapiData.pageUrl);



    return (
        <>
            <SEOHead 
                pageData={blog}
                configData={configData}
                pageUrl={strapiData.pageUrl}
                customSEO={{
                    title: blog?.seo?.metaTitle || seoData.title,
                    description: blog?.seo?.metaDescription || seoData.description,
                    canonical: blog?.seo?.canonicalURL || seoData.canonical,
                    openGraph: {
                        title: blog?.seo?.metaSocial?.[0]?.title || blog?.seo?.metaTitle || seoData.title,
                        description: blog?.seo?.metaSocial?.[0]?.description || blog?.seo?.metaDescription || seoData.description,
                        image: blog?.seo?.metaImage?.url || blog?.thumbnail?.url || seoData.openGraph.images[0]?.url,
                        type: 'article',
                        url: blog?.seo?.canonicalURL || seoData.canonical,
                    },
                    twitter: {
                        title: blog?.seo?.metaSocial?.[1]?.title || blog?.seo?.metaTitle || seoData.title,
                        description: blog?.seo?.metaSocial?.[1]?.description || blog?.seo?.metaDescription || seoData.description,
                        image: blog?.seo?.metaImage?.url || blog?.thumbnail?.url || seoData.twitter.images,
                    },
                    structuredData: blog?.seo?.structuredData || seoData.structuredData,
                }}
            />

            <HeaderSection data={header} pageUrl={strapiData.pageUrl}  />
            
            {/* Hero Section */}
            <section className={`${styles.heroSection} `}>
                <div className={styles.heroBackground}>
                    {/* {blog.thumbnail?.url && (
                        <OptimizedImage
                            src={blog?.seo?.metaImage?.url}
                            alt={blog.title}
                            fill
                            className={styles.heroImage}
                            priority
                            objectFit='fill'
                        />
                    )} */}
                </div>
                <div className={styles.heroOverlay}></div>
                <div className={`tw-container ${styles.heroContent}`}>
                    <h1 className={styles.heroTitle}>{blog.title}</h1>
                    <p className={styles.heroDescription}>{blog.Description}</p>
                    <div className={styles.heroMeta}>
                        <div className={styles.heroAuthor}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                            </svg>
                            <span>{blog.author?.name || blog.Author?.name || 'Unknown Author'}</span>
                        </div>
                        <div className={styles.heroDate}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                            </svg>
                            <span>{formatDate(blog.Date || blog.publishedAt)}</span>
                        </div>
                        <div className={styles.heroReadTime}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                            <span>{blog.ReadTime || '5'} min read</span>
                        </div>
                    </div>
                    {blog.categories && blog.categories.length > 0 && (
                        <div className={styles.heroTags}>
                            <div className={styles.heroTagsLabel}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                </svg>
                                <span>Categories</span>
                            </div>
                            {getCategoryNames(blog.categories).map((category, index) => (
                                <span key={index} className={styles.heroTag}>
                                    {category}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Breadcrumb */}
            <div >
                <div className="tw-container">
                    <Breadcrumb breadcrumbs={breadcrumbs} />
                </div>
            </div>

            {/* Main Content */}
            <main className={styles.mainContent}>
                <div className="tw-container">
                    <div className={styles.contentLayout}>
                        <TableOfContents 
                            tableOfContents={tableOfContents} 
                            activeHeading={activeHeading} 
                        />
                        
                        <BlogContent blog={blog} isLoading={isLoading} />
                        
                        <RelatedPosts relatedPosts={relatedBlogs} />
                    </div>
                </div>
            </main>

            {/* Scroll Navigation - Up/Down Arrows */}
            <ScrollNavigation />

            <FooterSection data={footer} />
        </>
    );
}

// Server-side data fetching
export async function getServerSideProps(context) {
    const { slug } = context.params;

    try {
        // Fetch all required data in parallel using existing API functions
        const [configDataResponse, blogResponse] = await Promise.all([
            fetchConfigPage(),
            fetchBlogBySlug(slug)
        ]);

        const blog = blogResponse?.data || null;

        // Check if blog doesn't exist (return as not found)
        if (!blog) {
            return {
                notFound: true
            };
        }

        // Return valid data
        return {
            props: {
                initialData: {
                    blog,
                    configData: configDataResponse || {},
                    pageUrl: `/blog/${slug}`
                },
                error: null,
            }
        };
    } catch (error) {
        console.error('Error fetching blog data:', error);
        // Return error (with null initialData)
        return {
            props: {
                initialData: null,
                error: error.message || 'An unexpected error occurred while loading this blog post.',
            }
        };
    }
}